import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import "../App.css";
import { getTokenInternal } from "./useToken";
import Nav from "./Navigation.js";
import LoginRequired from "./LoginRequired.js";
import LightsOutSolver from './LightsOutSolver';
import PaintingSolver from './PaintingSolver';
import Grid from './memoryGrid.js';
import RushHourSolverPage from './RushHourSolverPage.js';
import SliderSolverPage from './SliderSolverPage.js';

// TEMPORARY: routes canvas-consumed images through our own backend since the
// R2 public dev URL doesn't honor its CORS policy (needed for <canvas> to
// read pixel data). Swap this back to a direct R2 URL once images are
// served from a proper custom domain instead of the r2.dev URL.
function proxiedImageUrl(rawUrl) {
  return `${process.env.REACT_APP_API_HOST}/api/images/proxy?url=${encodeURIComponent(rawUrl)}`;
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function canvasToClipboard(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        reject(new Error("Could not create image"));
        return;
      }
      try {
        await navigator.clipboard.write([
          new window.ClipboardItem({ "image/png": blob }),
        ]);
        resolve();
      } catch (e) {
        reject(e);
      }
    }, "image/png");
  });
}

// Copies a single item's image to the clipboard with its name drawn on as a
// caption, so pasting into Discord gives one small labeled image instead of
// a big unfurled link embed.
async function copyLabeledImageToClipboard(imageUrl, label) {
  const img = await loadImage(imageUrl);
  const W = img.naturalWidth || 300;
  const imgH = img.naturalHeight || 300;
  const LABEL_H = 36;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = imgH + LABEL_H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#111827";
  ctx.fillRect(0, 0, W, imgH + LABEL_H);
  ctx.drawImage(img, 0, 0, W, imgH);
  ctx.fillStyle = "#ffffff";
  ctx.font = "bold 20px sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(label, W / 2, imgH + 24, W - 16);
  await canvasToClipboard(canvas);
}

// Draws an image centered/scaled to fit within a box without distorting its
// aspect ratio -- same idea as CSS object-fit: contain.
function drawContain(ctx, img, boxX, boxY, boxW, boxH) {
  const imgRatio = img.width / img.height;
  const boxRatio = boxW / boxH;
  let drawW, drawH;
  if (imgRatio > boxRatio) {
    drawW = boxW;
    drawH = boxW / imgRatio;
  } else {
    drawH = boxH;
    drawW = boxH * imgRatio;
  }
  const drawX = boxX + (boxW - drawW) / 2;
  const drawY = boxY + (boxH - drawH) / 2;
  ctx.drawImage(img, drawX, drawY, drawW, drawH);
}

// Composites a whole list of items (image + name) into one grid image and
// copies that single image to the clipboard. If showTypeHeaders is set,
// items must already be sorted so same-type items are adjacent -- a small
// header row gets drawn above each type's own block instead of one flat grid.
async function copyItemGridToClipboard(items, options = {}) {
  const { showTypeHeaders = false } = options;
  const CELL = 110;
  const LABEL_H = 22;
  const HEADER_H = 24;
  const cols = Math.min(6, Math.max(1, Math.ceil(Math.sqrt(items.length))));

  const blocks = [];
  if (showTypeHeaders) {
    let currentType = null;
    let currentItems = [];
    items.forEach((item) => {
      const t = item.type || "Other";
      if (t !== currentType) {
        if (currentItems.length) blocks.push({ header: currentType, items: currentItems });
        currentType = t;
        currentItems = [];
      }
      currentItems.push(item);
    });
    if (currentItems.length) blocks.push({ header: currentType, items: currentItems });
  } else {
    blocks.push({ header: null, items });
  }

  let totalHeight = 0;
  const blockLayouts = blocks.map((block) => {
    const rows = Math.ceil(block.items.length / cols);
    const headerH = block.header ? HEADER_H : 0;
    const top = totalHeight;
    totalHeight += headerH + rows * (CELL + LABEL_H);
    return { ...block, headerH, top };
  });

  const canvas = document.createElement("canvas");
  canvas.width = cols * CELL;
  canvas.height = totalHeight;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#111827";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const flatItems = blockLayouts.flatMap((b) => b.items);
  const imgs = await Promise.all(
    flatItems.map((item) => loadImage(item.url).catch(() => null))
  );

  let flatIndex = 0;
  blockLayouts.forEach((block) => {
    if (block.header) {
      ctx.fillStyle = "#c084fc";
      ctx.font = "bold 14px sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(`${block.header} (${block.items.length})`, 4, block.top + 17);
    }
    block.items.forEach((item, i) => {
      const img = imgs[flatIndex];
      flatIndex++;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = col * CELL;
      const y = block.top + block.headerH + row * (CELL + LABEL_H);

      ctx.strokeStyle = "#374151";
      ctx.strokeRect(x + 1, y + 1, CELL - 2, CELL - LABEL_H - 2);
      if (img) {
        drawContain(ctx, img, x + 4, y + 4, CELL - 8, CELL - LABEL_H - 8);
      }

      ctx.fillStyle = "#ffffff";
      ctx.font = "11px sans-serif";
      ctx.textAlign = "center";
      let name = item.name;
      while (name.length > 3 && ctx.measureText(name).width > CELL - 8) {
        name = name.slice(0, -1);
      }
      if (name !== item.name) name = name.slice(0, -1) + "…";
      ctx.fillText(name, x + CELL / 2, y + CELL - 6);
    });
  });

  await canvasToClipboard(canvas);
}

export default function MyItemsV2() {
  const { gameName } = useParams();
  const [gameId, setGameId] = useState(null);
  const [myItems, setMyItems] = useState([]);
  const [itemVisibility, setItemVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedEventNames, setSelectedEventNames] = useState(["Primary"]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedEntitlementFilterIds, setSelectedEntitlementFilterIds] = useState([]);
  const [newOnly, setNewOnly] = useState(false);
  const [showFiltersPanel, setShowFiltersPanel] = useState(false);
  const [uncheckedUsers, setUncheckedUsers] = useState([]);
  const [showUncheckedOnly, setShowUncheckedOnly] = useState(false);
  const [showCheckedOnly, setShowCheckedOnly] = useState(false);
  const [allFriends, setAllFriends] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [whoTrustsMeIds, setWhoTrustsMeIds] = useState([]);
  const [myClaimsByKey, setMyClaimsByKey] = useState({}); // "itemId-friendAccountId" -> claim id, from the server (not just this session)
  const [claimMessage, setClaimMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [modalImageSrc, setModalImageSrc] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [highlightedItemId, setHighlightedItemId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showModal1, setShowModal1] = useState(false);
const [showModal2, setShowModal2] = useState(false);
const [showModal3, setShowModal3] = useState(false);
const [showModal4, setShowModal4] = useState(false);
const [showModal5, setShowModal5] = useState(false);
const dropdownRef = useRef(null);
const [pendingClaims, setPendingClaims] = useState([]);
const [showClaimsModal, setShowClaimsModal] = useState(false);
const [claimsActionError, setClaimsActionError] = useState("");
const [hideUnobtainable, setHideUnobtainable] = useState(false);
const [showItemImages, setShowItemImages] = useState(false);
const [tokenChecked, setTokenChecked] = useState(false);
const [isLoggedIn, setIsLoggedIn] = useState(false);
const [shareModalConfig, setShareModalConfig] = useState(null); // { title, items } | null
const [shareCopyStatus, setShareCopyStatus] = useState("idle"); // idle | copying | copied | error
const [activeShareBatch, setActiveShareBatch] = useState(null); // which batch button triggered the current status
const [shareGroupMode, setShareGroupMode] = useState("all"); // all | type
const [showTypeLabels, setShowTypeLabels] = useState(false); // One Image mode only -- headers on/off, still sorted by type either way
const SHARE_BATCH_SIZE = 40;
const [singleCopyStatus, setSingleCopyStatus] = useState("idle");

  useEffect(() => {
    getTokenInternal().then(t => {
      setIsLoggedIn(!!t);
      setTokenChecked(true);
    });
  }, []);

  // --- Translation support ---
  // These maps look like {"Head": "Cabeza", "Hat": "Sombrero", ...} --
  // raw English name -> translated name in the user's language. Used ONLY
  // for display text (headers, dropdown labels). Every grouping/filtering
  // line below keeps using the raw English string from item.types /
  // item.event / item.maps, completely untouched, so translations can
  // never change which items land under which header.
  const [languageId, setLanguageId] = useState(1); // defaults to English
  const [typeTranslations, setTypeTranslations] = useState({});
  const [eventTranslations, setEventTranslations] = useState({});
  const [mapTranslations, setMapTranslations] = useState({});
  const [itemNameTranslations, setItemNameTranslations] = useState({});
  const [mapsNameTranslations, setMapsNameTranslations] = useState({});

  // Small helper: look up the translated label for a raw value, falling
  // back to the raw value itself if no translation map entry exists yet.
  const translateType = (rawType) => typeTranslations[rawType] || rawType;
  const translateEvent = (rawEvent) => eventTranslations[rawEvent] || rawEvent;
  const translateMap = (rawMap) => mapTranslations[rawMap] || rawMap;
  const translateItemName = (rawName) => itemNameTranslations[rawName] || rawName;
  const translateMapsName = (rawName) => mapsNameTranslations[rawName] || rawName;

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const token = await getTokenInternal();
        // Don't trust token.account.language_id -- it's baked into the JWT
        // at login time, so it goes stale the moment someone changes their
        // language in My Profile without logging back in. Look up the
        // live value instead.
        let accountLanguageId = 1;
        if (token?.account?.username) {
          const accountRes = await fetch(
            `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}`
          );
          if (accountRes.ok) {
            const accountData = await accountRes.json();
            accountLanguageId = accountData.language_id || 1;
            setHideUnobtainable(accountData.hide_unobtainable || false);
          setShowItemImages(accountData.show_item_images || false);
          }
        }
        setLanguageId(accountLanguageId);

        const [typesRes, eventRes, mapsRes, mapsNameRes, itemsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/types?language_id=${accountLanguageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/event?language_id=${accountLanguageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/maps?language_id=${accountLanguageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/maps_name?language_id=${accountLanguageId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/translations/lookup/items?language_id=${accountLanguageId}`),
        ]);

        if (typesRes.ok) setTypeTranslations(await typesRes.json());
        if (eventRes.ok) setEventTranslations(await eventRes.json());
        if (mapsRes.ok) setMapTranslations(await mapsRes.json());
        if (mapsNameRes.ok) setMapsNameTranslations(await mapsNameRes.json());
        if (itemsRes.ok) setItemNameTranslations(await itemsRes.json());
      } catch (error) {
        // Translations are a display nicety, not critical path -- if this
        // fails, headers just show the raw English string (same as before
        // this feature existed), so we only log, never block the page.
        console.error("Error fetching translations:", error.message);
      }
    };

    fetchTranslations();
  }, []);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_HOST}/api/games`)
      .then((res) => (res.ok ? res.json() : []))
      .then((games) => {
        const match = games.find((g) => g.name === gameName);
        if (match) setGameId(match.id);
      })
      .catch((err) => console.error("Error resolving game:", err));
  }, [gameName]);

  const fetchUserData = async () => {
    try {
      const token = await getTokenInternal();
      if (token) {
        const url = `${process.env.REACT_APP_API_HOST}/users/${token.account.id}/items`;
        const response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
        });

        if (response.ok) {
          const completeList = await response.json();

          const filteredList = completeList.filter(
            (item) => item.games_id === gameId
          );

          const initialVisibility = filteredList.reduce((acc, item) => {
            acc[item.name] = false;
            return acc;
          }, {});
          setItemVisibility(initialVisibility);
          setMyItems(filteredList);
        } else {
          console.error("Error fetching data:", response.statusText);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleEventFilter = (eventName) => {
    setSelectedEventNames((prev) =>
      prev.includes(eventName) ? prev.filter((e) => e !== eventName) : [...prev, eventName]
    );
  };

  const toggleAllEvents = (allSelected, names) => {
    setSelectedEventNames(allSelected ? [] : [...names]);
  };

  const toggleTypeFilter = (type) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const toggleEntitlementFilter = (reqId) => {
    setSelectedEntitlementFilterIds((prev) =>
      prev.includes(reqId) ? prev.filter((id) => id !== reqId) : [...prev, reqId]
    );
  };

  const clearAllFilters = () => {
    setSelectedEventNames(["Primary"]);
    setSelectedTypes([]);
    setSelectedEntitlementFilterIds([]);
    setNewOnly(false);
  };

  const toggleVisibility = (itemName) => {
    setItemVisibility((prevVisibility) => {
      const newVisibility = Object.keys(prevVisibility).reduce((acc, key) => {
        acc[key] = key === itemName ? !prevVisibility[key] : false;
        return acc;
      }, {});
      return newVisibility;
    });
  };

  const handleToggleChecked = async (userItemId, isChecked) => {
    try {
      const token = await getTokenInternal();

      if (token) {
        const selectedItem = myItems.find(
          (item) => item.user_item_id === userItemId
        );

        if (selectedItem) {
          const url = `${process.env.REACT_APP_API_HOST}/users/${token.account.id}/items/${userItemId}/update`;
          const response = await fetch(url, {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              checked: !isChecked,
            }),
          });

          if (response.ok) {
            setMyItems((prevItems) =>
              prevItems.map((item) =>
                item.user_item_id === userItemId
                  ? { ...item, checked: !isChecked }
                  : item
              )
            );
          } else {
            console.error("Error updating isChecked:", response.statusText);
          }
        } else {
          console.error("Item not found in myItems array");
        }
      }
    } catch (error) {
      console.error("Error updating isChecked:", error.message);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

    useEffect(() => {
    if (showModal1 || showModal2 || showModal3 || showModal4 || showModal5) {
      document.body.style.overflow = 'hidden'; // Prevent scrolling
    } else {
      document.body.style.overflow = 'auto'; // Allow scrolling when modals are closed
    }

    // Cleanup the effect when the component unmounts or modals are closed
    return () => {
      document.body.style.overflow = 'auto'; // Always allow scrolling when modals are closed
    };
  }, [showModal1, showModal2, showModal3, showModal4, showModal5]);

  useEffect(() => {
    const fetchFriends = async () => {
      try {
        const token = await getTokenInternal();

        if (token) {
          const url = `${process.env.REACT_APP_API_HOST}/api/friendsList`;
          const response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token.access_token}`,
              "Content-Type": "application/json",
            },
          });
          if (response.ok) {
            const data = await response.json();
            setAllFriends(data);
          }
        }
      } catch (error) {
        console.error("Error fetching pending friend requests:", error);
      }
    };

    fetchFriends();
  }, []);

  useEffect(() => {
    const fetchWhoTrustsMe = async () => {
      try {
        const token = await getTokenInternal();
        if (!token) return;
        setCurrentUserId(token.account.id);
        const response = await fetch(
          `${process.env.REACT_APP_API_HOST}/api/trusted-claimers/who-trusts-me`,
          { headers: { Authorization: `Bearer ${token.access_token}` } }
        );
        if (response.ok) {
          const data = await response.json();
          setWhoTrustsMeIds(data.map((account) => account.account_id));
        }
        await loadClaims();
        await loadMyClaims();
      } catch (error) {
        console.error("Error fetching trusted-by list:", error.message);
      }
    };
    fetchWhoTrustsMe();
  }, []);

  // --- Unlock requirements (preorder/DLC gating) ---
  const [requirementsByItem, setRequirementsByItem] = useState({});
  const [requirementsMap, setRequirementsMap] = useState({});
  const [myRequirementIds, setMyRequirementIds] = useState([]);
  const [showDlcModal, setShowDlcModal] = useState(false);
  const [dlcModalReqIds, setDlcModalReqIds] = useState([]);

  useEffect(() => {
    const fetchUnlockRequirements = async () => {
      try {
        const [byItemRes, allRequirementsRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/by-item?games_id=${gameId}`),
          fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements?games_id=${gameId}`),
        ]);
        if (byItemRes.ok) setRequirementsByItem(await byItemRes.json());
        if (allRequirementsRes.ok) {
          const allRequirements = await allRequirementsRes.json();
          const map = {};
          allRequirements.forEach((req) => { map[req.id] = req; });
          setRequirementsMap(map);
        }

        const token = await getTokenInternal();
        if (token) {
          const myRes = await fetch(`${process.env.REACT_APP_API_HOST}/api/unlock-requirements/my-requirements`, {
            headers: { Authorization: `Bearer ${token.access_token}` },
          });
          if (myRes.ok) setMyRequirementIds(await myRes.json());
        }
      } catch (error) {
        console.error("Error fetching unlock requirements:", error.message);
      }
    };
    if (gameId !== null) fetchUnlockRequirements();
  }, [gameId]);

  const describeRequirement = (req) => {
    if (!req) return null;
    const platformSuffix = req.platform ? ` on ${req.platform}` : "";
    if (req.requirement_type.toLowerCase() === "preorder") {
      return `This item was only available if you preordered${platformSuffix} (${req.name}), and can no longer be obtained.`;
    }
    if (req.requirement_type.toLowerCase() === "early access") {
      return `This item was only available during early access${platformSuffix} (${req.name}), and can no longer be obtained.`;
    }
    if (req.requirement_type.toLowerCase() === "dlc") {
      return `This item can be obtained via the ${req.name}${req.platform ? ` (${req.platform})` : ""} DLC.`;
    }
    return `This item requires: ${req.name}${req.platform ? ` (${req.platform})` : ""}.`;
  };

  const getLockInfo = (itemId) => {
    const requirementIds = requirementsByItem[itemId];
    if (!requirementIds || requirementIds.length === 0) return null;
    if (requirementIds.some((id) => myRequirementIds.includes(id))) return null;
    const descriptions = requirementIds
      .map((id) => describeRequirement(requirementsMap[id]))
      .filter(Boolean);
    return { requirementIds, descriptions };
  };

  const handleToggleRequirement = async (reqId, currentlyOwned) => {
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const method = currentlyOwned ? "DELETE" : "POST";
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/unlock-requirements/${reqId}/claim-mine`,
        { method, headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (res.ok) {
        setMyRequirementIds((prev) =>
          currentlyOwned ? prev.filter((id) => id !== reqId) : [...prev, reqId]
        );
      }
    } catch (error) {
      console.error("Error toggling requirement:", error.message);
    }
  };

  const loadClaims = async () => {
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/pending`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (res.ok) setPendingClaims(await res.json());
    } catch (error) {
      console.error("Error fetching claims:", error.message);
    }
  };

  // What *I've* already claimed for others -- keyed by item+friend so the
  // "Claim for them" buttons can show "Cancel" instead, based on real
  // server state rather than only what happened this session.
  const loadMyClaims = async () => {
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/made-by-me`,
        { headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        const map = {};
        data.forEach((c) => {
          map[`${c.item_id}-${c.claimed_for_account_id}`] = c.id;
        });
        setMyClaimsByKey(map);
      }
    } catch (error) {
      console.error("Error fetching my claims:", error.message);
    }
  };

  const handleClaimApprove = async (claim) => {
    setClaimsActionError("");
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claim.id}/approve`,
        { method: "PUT", headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (!res.ok) throw new Error("Could not approve claim");
      await loadClaims();
    } catch (error) {
      setClaimsActionError(error.message);
    }
  };

  const handleClaimReject = async (claim) => {
    setClaimsActionError("");
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claim.id}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (!res.ok) throw new Error("Could not reject claim");
      await loadClaims();
    } catch (error) {
      setClaimsActionError(error.message);
    }
  };

  const handleClaimApproveAll = async (claimedByAccountId) => {
    setClaimsActionError("");
    try {
      const token = await getTokenInternal();
      if (!token) return;
      const res = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/approve-all?claimed_by=${claimedByAccountId}`,
        { method: "PUT", headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (!res.ok) throw new Error("Could not approve claims");
      await loadClaims();
    } catch (error) {
      setClaimsActionError(error.message);
    }
  };

  const resolveFriendAccountId = (friend) =>
    friend.user_id !== currentUserId ? friend.user_id : friend.friend_id;

  const handleClaimForFriend = async (friendAccountId, itemId) => {
    setClaimMessage("");
    try {
      const token = await getTokenInternal();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/create`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            claimed_for_account_id: friendAccountId,
            item_id: itemId,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Could not submit claim");
      }
      setClaimMessage(`Claim submitted for "${data.item_name}". They still need to approve it.`);
      setMyClaimsByKey((prev) => ({
        ...prev,
        [`${data.item_id}-${data.claimed_for_account_id}`]: data.id,
      }));
    } catch (error) {
      setClaimMessage(error.message);
    }
  };

  const handleCancelClaimForFriend = async (claimId, itemId, targetAccountId) => {
    setClaimMessage("");
    try {
      const token = await getTokenInternal();
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/api/claims/${claimId}/cancel`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token.access_token}` } }
      );
      if (!response.ok) throw new Error("Could not cancel claim");
      setMyClaimsByKey((prev) => {
        const next = { ...prev };
        delete next[`${itemId}-${targetAccountId}`];
        return next;
      });
    } catch (error) {
      setClaimMessage(error.message);
    }
  };

  const handleButtonClick = async (item_id) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/items/${item_id}/users/unchecked`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch unchecked users");
      }
      const data = await response.json();
      setUncheckedUsers(data);
    } catch (error) {
      console.error(error.message);
    }
  };


  const combinedClickHandler = (item) => {
    setSelectedItem(item);                    // Shows the image/info box
    toggleVisibility(item.name);              // Shows "Who needs it?"
    handleButtonClick(item.item_id);     // Any other logic
    setHighlightedItemId(item.user_item_id)
    setClaimMessage("");
    const lockInfo = getLockInfo(item.item_id);
    if (lockInfo) {
      setDlcModalReqIds(lockInfo.requirementIds);
      setShowDlcModal(true);
    }
  };


  useEffect(() => {
    if (gameId !== null) fetchUserData();
  }, [gameId]);

  if (loading) {
    return <p>Loading...</p>;
  }

  const filterItemsBySearchQuery = (items) => {
    const q = searchQuery.toLowerCase();
    return items.filter((item) =>
      item.name.toLowerCase().includes(q) ||
      translateItemName(item.name).toLowerCase().includes(q)
    );
  };

  // Type / entitlement / "new only" filters from the Filters panel. Event
  // selection is handled separately below since it drives which sections
  // render, not just which items within them.
  const matchesTypeFilter = (item) => selectedTypes.length === 0 || selectedTypes.includes(item.types);
  const matchesEntitlementFilter = (item) => {
    if (selectedEntitlementFilterIds.length === 0) return true;
    const reqIds = requirementsByItem[item.item_id] || [];
    return reqIds.some((id) => selectedEntitlementFilterIds.includes(id));
  };
  const matchesNewFilter = (item) => !newOnly || item.is_new;
  const matchesSidebarFilters = (item) =>
    matchesTypeFilter(item) && matchesEntitlementFilter(item) && matchesNewFilter(item);

  // Items not yet in the regular pool -- private-testing-only or fully
  // inactive/draft. These get pulled out of normal event/Primary grouping
  // and shown in their own section above everything else, with their own
  // "(x/y)" count there. Once an item is made active and non-public, it
  // stops matching this filter and falls straight into its normal
  // event/Primary group on the next render -- no extra bookkeeping needed.
  const isNotYetPublic = (item) => item.private_testing || item.is_active === false;

  // Locked (gated, not-yet-owned) items are still shown so people can see
  // what they're missing, but they don't count toward "found" totals --
  // there's nothing to "find" for an item you can't obtain.
  const obtainableItems = myItems.filter((item) => !getLockInfo(item.item_id) && matchesSidebarFilters(item));

  // The headline total only covers items everyone can actually see -- not
  // yet public items get their own count in their own section instead, so
  // they're excluded here to avoid double-counting / confusing the number.
  const publiclyObtainableItems = obtainableItems.filter((item) => !isNotYetPublic(item));
  const totalItemsCount = publiclyObtainableItems.length;
  const checkedItemsCount = publiclyObtainableItems.filter((item) => item.checked).length;

  const baseItems = (hideUnobtainable
    ? myItems.filter((item) => !getLockInfo(item.item_id))
    : myItems
  ).filter(matchesSidebarFilters);

  const filteredItemsToRender = showUncheckedOnly
    ? filterItemsBySearchQuery(baseItems).filter((item) => !item.checked)
    : showCheckedOnly
    ? filterItemsBySearchQuery(baseItems).filter((item) => item.checked)
    : filterItemsBySearchQuery(baseItems);

  const pinnedItems = filteredItemsToRender.filter(isNotYetPublic);
  const pinnedItemsAll = obtainableItems.filter(isNotYetPublic);

  const nonEventItems = filteredItemsToRender.filter(
    (item) => item.event === "Primary" && !isNotYetPublic(item)
  );
  // "All" (unfiltered by the Found/Not Found toggle) counterparts of the
  // sections above, used ONLY for the "(x/y)" header counts so those counts
  // stay anchored to the grand total instead of shrinking/growing as people
  // toggle Found/Not Found -- same behavior as the top-level total.
  const nonEventItemsAll = obtainableItems.filter(
    (item) => item.event === "Primary" && !isNotYetPublic(item)
  );
  const selectedEvents = filteredItemsToRender.filter(
    (item) => item.event !== "Primary" && selectedEventNames.includes(item.event) && !isNotYetPublic(item)
  );
  const selectedEventsAll = obtainableItems.filter(
    (item) => item.event !== "Primary" && selectedEventNames.includes(item.event) && !isNotYetPublic(item)
  );

  // Full, filter-independent option lists for the Filters panel -- so
  // unchecking something doesn't make its own checkbox disappear.
  const allEventNames = Array.from(
    new Set(myItems.filter((item) => !isNotYetPublic(item)).map((item) => item.event))
  ).sort((a, b) => (a === "Primary" ? -1 : b === "Primary" ? 1 : a.localeCompare(b)));
  const allTypeNames = Array.from(
    new Set(myItems.filter((item) => !isNotYetPublic(item)).map((item) => item.types))
  ).sort();
  const allEventsSelected =
    allEventNames.length > 0 && allEventNames.every((e) => selectedEventNames.includes(e));
  const eventsMatchDefault =
    selectedEventNames.length === 1 && selectedEventNames[0] === "Primary";
  const activeFilterCount =
    selectedTypes.length +
    selectedEntitlementFilterIds.length +
    (newOnly ? 1 : 0) +
    (eventsMatchDefault ? 0 : 1);

  // What's still needed, regardless of the Found/Not Found toggle above --
  // used to feed the per-event/Primary "Share Missing Items" popups.
  const missingPrimaryItems = nonEventItemsAll.filter((item) => !item.checked);

  const groupItemsByEvent = (items) => {
    const groupedItems = items.reduce((acc, item) => {
      if (!acc[item.event]) {
        acc[item.event] = [];
      }
      acc[item.event].push(item);
      return acc;
    }, {});

    const sortedEvents = Object.keys(groupedItems).sort();

    return sortedEvents.map((event) => ({
      event,
      items: groupedItems[event],
    }));
  };

  const groupedSelectedEvents = groupItemsByEvent(selectedEvents);
  const groupedAllByEvent = Object.fromEntries(
    groupItemsByEvent(selectedEventsAll).map((group) => [group.event, group.items])
  );

  const handleToggleShowChecked = () => {
    setShowUncheckedOnly(false);
    setShowCheckedOnly(!showCheckedOnly);
  };

  const handleToggleShowUnchecked = () => {
    setShowCheckedOnly(false);
    setShowUncheckedOnly(!showUncheckedOnly);
  };

  const showModal = (imageSrc) => {
    setModalImageSrc(imageSrc);
  };

  const hideModal = () => {
    setModalImageSrc(null);
  };

  if (tokenChecked && !isLoggedIn) return <LoginRequired />;

  return (
    <div className="text-white">
      <Nav />
      <div className="container mx-auto p-4 flex flex-col sm:flex-row gap-4">
        {/* Left Column - Item List */}
        <div className="flex-1">
          {/* Event info + Legend */}
          <div className="mb-4 border border-gray-600 rounded-lg p-3">
            <div className="text-2xl font-bold mb-3">
              Use the Filters button to pick events, item types, and more!
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-gray-300">
              <div className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-neutral-400 flex-shrink-0"></span>
                Previously released item
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-green-500 flex-shrink-0"></span>
                Previously released, claimed
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-amber-200 flex-shrink-0"></span>
                New this event
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded bg-sky-400 flex-shrink-0"></span>
                New this event, claimed
              </div>
            </div>
          </div>

          {/* Filters trigger */}
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <button
              onClick={() => setShowFiltersPanel(true)}
              className="flex items-center gap-2 px-3 py-2 border border-gray-500 rounded-lg bg-gray-800 hover:bg-gray-700 font-semibold"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4 text-gray-400">
                <path d="M4 6h16M7 12h10M10 18h4"/>
              </svg>
              Events &amp; Filters
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-bold bg-blue-600 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </button>
            <span className="text-sm text-gray-400">
              {selectedEventNames.length > 0 ? selectedEventNames.map(translateEvent).join(", ") : "No events selected"}
            </span>
          </div>

          {/* Pending Claims Badge */}
          {pendingClaims.length > 0 && (
            <button
              onClick={() => setShowClaimsModal(true)}
              className="mb-4 flex items-center gap-2 px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-white font-semibold"
            >
              Pending Claims
              <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-800 rounded-full min-w-[18px]">
                {pendingClaims.length}
              </span>
            </button>
          )}

          {/* Search Box */}
          <div className="mb-4">
            <input
              type="text"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="p-1 border rounded text-black w-full max-w-xs"
            />
          </div>

          {/* Progress */}
          <div className="text-lg font-bold mb-4">
            {checkedItemsCount >= totalItemsCount
              ? "Collected All Items!!!!!"
              : `Total: ${checkedItemsCount}/${totalItemsCount} items found`}
          </div>

          {/* Not Yet Public -- private-testing / inactive items, pinned above everything else */}
          {pinnedItems.length > 0 && (
            <div className="mb-6 border-2 border-purple-500 rounded-lg p-3 bg-neutral-900">
              <p className="font-bold text-xl underline italic text-purple-300">
                Not Yet Public ({pinnedItemsAll.filter((i) => i.checked).length}/{pinnedItemsAll.length})
              </p>
              <div className="grid gap-2 mt-2">
                {filterItemsBySearchQuery(pinnedItems)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((filteredItem) => {
                    const lockInfo = getLockInfo(filteredItem.item_id);
                    return (
                      <div
                        key={filteredItem.user_item_id}
                        className={`border p-1 rounded flex items-center ${
                          lockInfo ? "bg-gray-700 opacity-60" : filteredItem.is_new ? (filteredItem.checked ? "bg-sky-400" : "bg-amber-200 text-gray-800") : (filteredItem.checked ? "bg-green-500" : "bg-neutral-400")
                        } ${highlightedItemId === filteredItem.user_item_id ? "ring-4 ring-yellow-300" : ""}`}
                      >
                        <input
                          type="checkbox"
                          checked={filteredItem.checked}
                          disabled={!!lockInfo}
                          onChange={() => handleToggleChecked(filteredItem.user_item_id, filteredItem.checked)}
                          className="h-4 w-4 shrink-0"
                        />
                        {showItemImages && filteredItem.url && (
                          <img
                            src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${filteredItem.url}`}
                            alt=""
                            loading="lazy"
                            className="ml-2 shrink-0 rounded object-contain"
                            style={{ width: 48, height: 48 }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        )}
                        <div className="ml-2 flex-1 flex items-center gap-2 flex-wrap">
                          {lockInfo && <span className="mr-1" title="Locked">🔒</span>}
                          {translateItemName(filteredItem.name)}
                          {filteredItem.is_active === false && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-800 text-gray-300 font-semibold">
                              Inactive
                            </span>
                          )}
                          {filteredItem.private_testing && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-purple-800 text-purple-200 font-semibold">
                              Private Testing
                            </span>
                          )}
                        </div>
                        <button
                          className="ml-2 shrink-0 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                          onClick={() => combinedClickHandler(filteredItem)}
                        >
                          Info
                        </button>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Event Items */}
          {groupedSelectedEvents.map(({ event, items }) => {
            const allItemsForEvent = groupedAllByEvent[event] || [];
            const isOther = items.some(i => i.acquisition_method_name);

            const renderItemRow = (filteredItem) => {
              const lockInfo = getLockInfo(filteredItem.item_id);
              return (
                <div
                  key={filteredItem.user_item_id}
                  className={`border p-1 rounded flex items-center ${
                    lockInfo ? "bg-gray-700 opacity-60" : filteredItem.is_new ? (filteredItem.checked ? "bg-sky-400" : "bg-amber-200 text-gray-800") : (filteredItem.checked ? "bg-green-500" : "bg-neutral-400")
                  } ${highlightedItemId === filteredItem.user_item_id ? "ring-4 ring-yellow-300" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={filteredItem.checked}
                    disabled={!!lockInfo}
                    onChange={() => handleToggleChecked(filteredItem.user_item_id, filteredItem.checked)}
                    className="h-4 w-4 shrink-0"
                  />
                  {showItemImages && filteredItem.url && (
                    <img
                      src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${filteredItem.url}`}
                      alt=""
                      loading="lazy"
                      className="ml-2 shrink-0 rounded object-contain"
                      style={{ width: 48, height: 48 }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="ml-2 flex-1 flex items-center">
                    {lockInfo && <span className="mr-1" title="Locked">🔒</span>}
                    {translateItemName(filteredItem.name)}
                  </div>
                  <button
                    className="ml-2 shrink-0 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                    onClick={() => combinedClickHandler(filteredItem)}
                  >
                    Info
                  </button>
                </div>
              );
            };

            if (isOther) {
              // Outer group: acquisition_method_name; inner group: types
              const methods = Array.from(new Set(items.map(i => i.acquisition_method_name).filter(Boolean))).sort();
              const itemsWithoutMethod = items.filter(i => !i.acquisition_method_name);

              const renderTypeGroups = (subItems, subAllItems) => {
                const types = Array.from(new Set(subItems.map(i => i.types)));
                return types.map(type => {
                  const typeItems = subItems.filter(i => i.types === type);
                  const allTypeItems = subAllItems.filter(i => i.types === type);
                  return (
                    <div key={type} className="mb-4 mr-2">
                      <h5 className="text-md font-bold mb-2">
                        {translateType(type)} ({allTypeItems.filter(i => i.checked).length}/{allTypeItems.length})
                      </h5>
                      <div className="grid gap-2">
                        {filterItemsBySearchQuery(typeItems)
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(renderItemRow)}
                      </div>
                    </div>
                  );
                });
              };

              return (
                <div key={event}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-bold italic underline">{translateEvent(event)}</h3>
                    {(() => {
                      const missingForThisEvent = allItemsForEvent.filter(i => !i.checked);
                      return missingForThisEvent.length > 0 && (
                        <button
                          onClick={() => {
                            setShareCopyStatus("idle");
                            setShareGroupMode("all");
                            setShareModalConfig({
                              title: `Share ${translateEvent(event)} Missing Items`,
                              items: missingForThisEvent,
                            });
                          }}
                          className="text-xs p-1 border rounded bg-purple-800 hover:bg-purple-700"
                        >
                          Share Missing ({missingForThisEvent.length})
                        </button>
                      );
                    })()}
                  </div>
                  <div className="text-lg font-bold mb-2 ml-2">
                    Items found: {allItemsForEvent.filter(i => i.checked).length}/{allItemsForEvent.length}
                  </div>
                  {methods.map(method => {
                    const methodItems = items.filter(i => i.acquisition_method_name === method);
                    const allMethodItems = allItemsForEvent.filter(i => i.acquisition_method_name === method);
                    return (
                      <div key={method} className="mb-4 ml-2">
                        <h4 className="text-md font-bold mb-2 italic underline">{method} ({allMethodItems.filter(i => i.checked).length}/{allMethodItems.length})</h4>
                        <div className="flex flex-row flex-wrap">
                          {renderTypeGroups(methodItems, allMethodItems)}
                        </div>
                      </div>
                    );
                  })}
                  {itemsWithoutMethod.length > 0 && (
                    <div className="flex flex-row flex-wrap ml-2">
                      {renderTypeGroups(itemsWithoutMethod, allItemsForEvent.filter(i => !i.acquisition_method_name))}
                    </div>
                  )}
                </div>
              );
            }

            // All other events: group by types only
            const groupValues = Array.from(new Set(items.map(i => i.types)));
            return (
              <div key={event}>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold italic underline">{translateEvent(event)}</h3>
                  {(() => {
                    const missingForThisEvent = allItemsForEvent.filter(i => !i.checked);
                    return missingForThisEvent.length > 0 && (
                      <button
                        onClick={() => {
                          setShareCopyStatus("idle");
                          setShareGroupMode("all");
                          setShareModalConfig({
                            title: `Share ${translateEvent(event)} Missing Items`,
                            items: missingForThisEvent,
                          });
                        }}
                        className="text-xs p-1 border rounded bg-purple-800 hover:bg-purple-700"
                      >
                        Share Missing ({missingForThisEvent.length})
                      </button>
                    );
                  })()}
                </div>
                <div className="text-lg font-bold mb-2 ml-2">
                  Items found: {allItemsForEvent.filter(i => i.checked).length}/{allItemsForEvent.length}
                </div>
                <div className="flex flex-row flex-wrap mb-2">
                  {groupValues.map((group) => {
                    const groupItems = items.filter(i => i.types === group);
                    const allGroupItems = allItemsForEvent.filter(i => i.types === group);
                    return (
                      <div key={group} className="mb-4 mb-6 ml-2">
                        <h4 className="text-md font-bold mb-2">
                          {translateType(group)} ({allGroupItems.filter(i => i.checked).length}/{allGroupItems.length})
                        </h4>
                        <div className="grid gap-2">
                          {filterItemsBySearchQuery(groupItems)
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(renderItemRow)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Non-Event Items (Primary) */}
          {selectedEventNames.includes("Primary") && (
          <>
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-xl underline italic">{translateEvent("Primary")}</p>
            {missingPrimaryItems.length > 0 && (
              <button
                onClick={() => {
                  setShareCopyStatus("idle");
                  setShareGroupMode("all");
                  setShareModalConfig({
                    title: "Share Primary Missing Items",
                    items: missingPrimaryItems,
                  });
                }}
                className="text-xs p-1 border rounded bg-purple-800 hover:bg-purple-700"
              >
                Share Missing ({missingPrimaryItems.length})
              </button>
            )}
          </div>
          <div className="text-lg font-bold mb-2 ml-2">
            Items found: {nonEventItemsAll.filter(i => i.checked).length}/{nonEventItemsAll.length}
          </div>
          {Array.from(new Set(nonEventItems.map(i => i.maps))).map(map => (
            <div key={map} className="mb-6 ml-2">
              <h3 className="text-lg font-bold mb-2 italic underline">{translateMap(map)}</h3>
              <div className="flex flex-row flex-wrap mb-4">
                {Array.from(new Set(nonEventItems.filter(i => i.maps === map).map(i => i.types))).map(type => (
                  <div key={type} className="mr-2">
                    <h4 className="text-md font-bold mb-2">
                      {translateType(type)} ({nonEventItemsAll.filter(i => i.maps === map && i.types === type && i.checked).length}/{nonEventItemsAll.filter(i => i.maps === map && i.types === type).length})
                    </h4>
                    <div className="grid grid-cols-1 gap-2">
                      {nonEventItems
                        .filter(i => i.maps === map && i.types === type)
                        .map(filteredItem => {
                          const lockInfo = getLockInfo(filteredItem.item_id);
                          return (
                          <div
                            key={filteredItem.user_item_id}
                            className={`border p-1 rounded flex items-center ${
                              lockInfo ? "bg-gray-700 opacity-60" : filteredItem.is_new ? (filteredItem.checked ? "bg-sky-400" : "bg-amber-200 text-gray-800") : (filteredItem.checked ? "bg-green-500" : "bg-neutral-400")
                            } ${highlightedItemId === filteredItem.user_item_id ? "ring-4 ring-yellow-300" : ""}`}
                          >
                            <input
                              type="checkbox"
                              checked={filteredItem.checked}
                              disabled={!!lockInfo}
                              onChange={() => handleToggleChecked(filteredItem.user_item_id, filteredItem.checked)}
                              className="h-4 w-4 shrink-0"
                            />
                            {showItemImages && filteredItem.url && (
                              <img
                                src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${filteredItem.url}`}
                                alt=""
                                loading="lazy"
                                className="ml-2 shrink-0 rounded object-contain"
                                style={{ width: 48, height: 48 }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div className="ml-2 flex-1 flex items-center">
                              {lockInfo && <span className="mr-1" title="Locked">🔒</span>}
                              {translateItemName(filteredItem.name)}
                            </div>
                            <button
                              className="ml-2 shrink-0 bg-blue-500 border-solid border-2 border-white text-white px-1 py-0 rounded"
                              onClick={() => combinedClickHandler(filteredItem)}
                            >
                              Info
                            </button>
                          </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          </>
          )}
        </div>

        {/* Right Column - Item Detail Panel */}
        <div className="flex flex-col items-start w-full sm:w-[300px]">
          {selectedItem && (
            <div className="w-full sm:w-[300px] p-4 bg-neutral-800 rounded-lg text-white border border-gray-500 sm:sticky sm:top-4 self-start max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg font-bold mb-1">{translateItemName(selectedItem.name)}</h2>
              {translateItemName(selectedItem.name) !== selectedItem.name && (
                <p className="text-xs text-gray-400 mb-2">{selectedItem.name}</p>
              )}
              <div className="mb-2">
                Map: {translateMapsName(selectedItem.maps_name)}
                {translateMapsName(selectedItem.maps_name) !== selectedItem.maps_name && (
                  <span className="text-xs text-gray-400 ml-1">({selectedItem.maps_name})</span>
                )}
              </div>
              {/* Entitlement / lock info */}
              {(() => {
                const reqIds = requirementsByItem[selectedItem.item_id];
                if (!reqIds || reqIds.length === 0) return null;
                const lockInfo = getLockInfo(selectedItem.item_id);
                return (
                  <div className={`mb-3 p-2 rounded text-sm border ${lockInfo ? "bg-yellow-900 bg-opacity-40 border-yellow-600 text-yellow-200" : "bg-green-900 bg-opacity-40 border-green-600 text-green-200"}`}>
                    {lockInfo ? "🔒 " : "✓ "}
                    {reqIds.map((reqId) => {
                      const req = requirementsMap[reqId];
                      if (!req) return null;
                      return (
                        <span key={reqId}>
                          <strong>{req.name}{req.platform ? ` (${req.platform})` : ""}</strong>
                          {" — "}{req.is_available ? "Still available" : "No longer obtainable"}
                        </span>
                      );
                    })}
                  </div>
                );
              })()}
              <img
                className="object-scale-down h-48 w-full mb-2 cursor-pointer"
                src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${selectedItem.url}`}
                alt="Item"
                onClick={() => showModal(`${process.env.REACT_APP_R2_PUBLIC_URL}/${selectedItem.url}`)}
                onError={(e) => {
                  e.target.src = '/gamelogos/Unofficial.PNG';
                }}
              />
              <div className="text-center text-xs text-gray-400 mb-1">Click image for full size</div>
              <button
                className="w-full mb-4 p-1 border rounded text-sm hover:bg-gray-700"
                disabled={singleCopyStatus === "copying"}
                onClick={async () => {
                  const url = `${process.env.REACT_APP_R2_PUBLIC_URL}/${selectedItem.url}`;
                  const translatedName = translateItemName(selectedItem.name);
                  const copyName = translatedName !== selectedItem.name
                    ? `${translatedName} (${selectedItem.name})`
                    : translatedName;
                  setSingleCopyStatus("copying");
                  try {
                    await copyLabeledImageToClipboard(proxiedImageUrl(url), copyName);
                    setSingleCopyStatus("copied");
                    setTimeout(() => setSingleCopyStatus("idle"), 2000);
                  } catch (e) {
                    setSingleCopyStatus("error");
                    setTimeout(() => setSingleCopyStatus("idle"), 2000);
                  }
                }}
              >
                {singleCopyStatus === "copying"
                  ? "Copying..."
                  : singleCopyStatus === "copied"
                  ? "Copied!"
                  : singleCopyStatus === "error"
                  ? "Couldn't copy image"
                  : "Copy Image"}
              </button>
              {uncheckedUsers.length > 0 && (
                <div>
                  <h3 className="font-bold mb-1">Who needs Cosmetic:</h3>
                  {claimMessage && <p className="text-yellow-300 text-xs mb-1">{claimMessage}</p>}
                  <ul className="text-sm">
                    {allFriends
                      .filter(f => uncheckedUsers.includes(f.friend_username))
                      .sort((a, b) =>
                        uncheckedUsers.indexOf(a.friend_username) -
                        uncheckedUsers.indexOf(b.friend_username)
                      )
                      .map((friend, index) => {
                        const friendAccountId = resolveFriendAccountId(friend);
                        const friendTrustsMe = whoTrustsMeIds.includes(friendAccountId);
                        const claimKey = `${selectedItem.item_id}-${friendAccountId}`;
                        const existingClaimId = myClaimsByKey[claimKey];
                        return (
                          <li key={index} className="mb-1">
                            <div className="flex items-center justify-between">
                              <span>{friend.friend_username}</span>
                              {friendTrustsMe && (
                                existingClaimId ? (
                                  <button
                                    className="ml-2 bg-amber-600 border-solid border-2 border-white text-white px-1 py-0 rounded text-xs"
                                    onClick={() => handleCancelClaimForFriend(existingClaimId, selectedItem.item_id, friendAccountId)}
                                  >
                                    Unclaim
                                  </button>
                                ) : (
                                  <button
                                    className="ml-2 bg-green-600 border-solid border-2 border-white text-white px-1 py-0 rounded text-xs"
                                    onClick={() => handleClaimForFriend(friendAccountId, selectedItem.item_id)}
                                  >
                                    Claim for them
                                  </button>
                                )
                              )}
                            </div>
                            <div className="ml-2 text-xs">Discord: {friend.visible ? friend.discord_name : 'N/A'}</div>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fullscreen Modal for Item Image */}
      {modalImageSrc && (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black bg-opacity-75 z-50 p-4" onClick={hideModal}>
          {selectedItem && (
            <p className="text-white font-bold text-lg mb-3 drop-shadow">
              {translateItemName(selectedItem.name)}
            </p>
          )}
          <img src={modalImageSrc} alt="Modal Content" className="max-w-[90vw] max-h-[85vh] object-contain" />
        </div>
      )}

      {/* Share Missing Items Modal */}
      {shareModalConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-start z-50 overflow-y-auto pt-16" onClick={() => setShareModalConfig(null)}>
          <div className="bg-gray-800 rounded shadow-lg max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Fixed header -- never scrolls */}
            <div className="p-6 pb-3 shrink-0">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">
                  {shareModalConfig.title} ({shareModalConfig.items.length})
                </h2>
                <button onClick={() => setShareModalConfig(null)} className="text-gray-400 hover:text-white text-xl">✕</button>
              </div>
              <p className="text-gray-400 text-sm mb-3">
                Copies one compact image with all of these to your clipboard --
                just paste (Ctrl+V) straight into Discord.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShareGroupMode("all")}
                  className={`text-xs px-2 py-1 rounded border ${
                    shareGroupMode === "all"
                      ? "bg-purple-700 border-purple-500"
                      : "bg-gray-900 border-gray-600 text-gray-300"
                  }`}
                >
                  One Image
                </button>
                <button
                  onClick={() => setShareGroupMode("type")}
                  className={`text-xs px-2 py-1 rounded border ${
                    shareGroupMode === "type"
                      ? "bg-purple-700 border-purple-500"
                      : "bg-gray-900 border-gray-600 text-gray-300"
                  }`}
                >
                  Split by Type
                </button>
                {shareGroupMode === "all" && (
                  <label className="flex items-center gap-1.5 text-xs text-gray-300 ml-1 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={showTypeLabels}
                      onChange={(e) => setShowTypeLabels(e.target.checked)}
                      className="h-3.5 w-3.5"
                    />
                    Show type labels
                  </label>
                )}
              </div>
            </div>

            {/* Scrollable preview grid -- this is the only part that scrolls */}
            <div className="px-6 flex-1 overflow-y-auto min-h-0">
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4 bg-gray-900 rounded p-3">
                {shareModalConfig.items.map((item) => (
                  <div key={item.item_id} className="flex flex-col items-center text-center">
                    <img
                      src={`${process.env.REACT_APP_R2_PUBLIC_URL}/${item.url}`}
                      alt={translateItemName(item.name)}
                      className="w-16 h-16 object-cover rounded border border-gray-600"
                      onError={(e) => { e.target.src = '/gamelogos/Unofficial.PNG'; }}
                    />
                    <span className="text-[10px] text-gray-300 mt-1 truncate w-full">
                      {translateItemName(item.name)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer -- capped and independently scrollable, so a long list
                of per-type copy buttons can't push past the card's bounds */}
            <div className="p-6 pt-3 shrink-0 border-t border-gray-700 max-h-[45vh] overflow-y-auto">
            {shareCopyStatus === "error" && (
              <p className="text-red-400 text-sm mb-2">
                Couldn't copy the image -- your browser may not support image clipboard access.
              </p>
            )}
            {(() => {
              const doCopy = async (batchItems, batchKey) => {
                setActiveShareBatch(batchKey);
                setShareCopyStatus("copying");
                try {
                  await copyItemGridToClipboard(
                    batchItems.map((item) => ({
                      url: proxiedImageUrl(`${process.env.REACT_APP_R2_PUBLIC_URL}/${item.url}`),
                      name: translateItemName(item.name),
                      type: translateType(item.types || "Other"),
                    })),
                    { showTypeHeaders: shareGroupMode === "all" && showTypeLabels }
                  );
                  setShareCopyStatus("copied");
                  setTimeout(() => { setShareCopyStatus("idle"); setActiveShareBatch(null); }, 2000);
                } catch (e) {
                  setShareCopyStatus("error");
                  setTimeout(() => { setShareCopyStatus("idle"); setActiveShareBatch(null); }, 2000);
                }
              };

              const labelFor = (batchKey, defaultLabel) => {
                if (activeShareBatch !== batchKey) return defaultLabel;
                if (shareCopyStatus === "copying") return "Copying...";
                if (shareCopyStatus === "copied") return "Copied!";
                return defaultLabel;
              };

              const chunk = (arr) => {
                const out = [];
                for (let i = 0; i < arr.length; i += SHARE_BATCH_SIZE) {
                  out.push(arr.slice(i, i + SHARE_BATCH_SIZE));
                }
                return out;
              };

              // "Split by Type" groups items by their type first, then
              // still applies the same 40-item cap within each type --
              // a single type with 40+ items still gets its own batches.
              let sections;
              if (shareGroupMode === "type") {
                const byType = {};
                shareModalConfig.items.forEach((item) => {
                  const key = item.types || "Other";
                  if (!byType[key]) byType[key] = [];
                  byType[key].push(item);
                });
                sections = Object.keys(byType)
                  .sort((a, b) => translateType(a).localeCompare(translateType(b)))
                  .map((type) => ({ label: translateType(type), items: byType[type] }));
              } else {
                // Sort by type so same-type items land next to each other
                // in the grid, with a header drawn above each type's block
                // (see showTypeHeaders in copyItemGridToClipboard) instead
                // of a randomly-ordered flat grid.
                const sortedByType = [...shareModalConfig.items].sort((a, b) =>
                  translateType(a.types || "Other").localeCompare(translateType(b.types || "Other"))
                );
                sections = [{ label: null, items: sortedByType }];
              }

              return (
                <div className="flex flex-col gap-3">
                  {sections.map((section, sectionIdx) => {
                    const batches = chunk(section.items);
                    const singleBatch = batches.length <= 1;
                    return (
                      <div key={section.label || "all"}>
                        {section.label && (
                          <div className="text-sm font-semibold text-gray-300 mb-1">
                            {section.label} ({section.items.length})
                          </div>
                        )}
                        {!singleBatch && (
                          <p className="text-gray-400 text-xs mb-1">
                            That's a lot to fit in one readable image -- split into batches below.
                          </p>
                        )}
                        <div className={singleBatch ? "" : "grid grid-cols-2 sm:grid-cols-3 gap-2"}>
                          {batches.map((batchItems, i) => {
                            const batchKey = `${sectionIdx}-${i}`;
                            const start = i * SHARE_BATCH_SIZE + 1;
                            const end = i * SHARE_BATCH_SIZE + batchItems.length;
                            const defaultLabel = singleBatch
                              ? (section.label ? `Copy ${section.label}` : "Copy Image")
                              : `Copy ${section.label ? section.label + " " : ""}Items ${start}-${end}`;
                            return (
                              <button
                                key={batchKey}
                                onClick={() => doCopy(batchItems, batchKey)}
                                disabled={shareCopyStatus === "copying"}
                                className={`p-2 rounded bg-purple-700 hover:bg-purple-600 text-white font-semibold text-sm ${singleBatch ? "w-full" : ""}`}
                              >
                                {labelFor(batchKey, defaultLabel)}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            </div>
          </div>
        </div>
      )}

      {/* Pending Claims Modal */}
      {showClaimsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-start z-50 overflow-y-auto pt-16">
          <div className="bg-gray-800 p-6 rounded shadow-lg max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-white">Pending Claims For Me</h2>
              <button onClick={() => { setShowClaimsModal(false); setClaimsActionError(""); }} className="text-gray-400 hover:text-white text-xl">✕</button>
            </div>
            {(() => {
              const byClaimer = pendingClaims.reduce((acc, c) => {
                acc[c.claimed_by_account_id] = acc[c.claimed_by_account_id] || { username: c.claimed_by_username, count: 0 };
                acc[c.claimed_by_account_id].count += 1;
                return acc;
              }, {});
              return (
                <>
                  {Object.keys(byClaimer).length > 0 && (
                    <div className="mb-4">
                      {Object.entries(byClaimer).map(([claimerId, info]) => (
                        <button
                          key={claimerId}
                          onClick={() => handleClaimApproveAll(claimerId)}
                          className="mr-2 mb-2 px-3 py-1 bg-green-700 hover:bg-green-600 rounded text-sm text-white"
                        >
                          Approve all {info.count} from {info.username}
                        </button>
                      ))}
                    </div>
                  )}
                  {pendingClaims.length === 0 ? (
                    <p className="text-gray-300">No pending claims.</p>
                  ) : (
                    <table className="w-full text-left text-sm text-white">
                      <thead>
                        <tr className="border-b border-gray-600">
                          <th className="pr-4 pb-2">Item</th>
                          <th className="pr-4 pb-2">Claimed By</th>
                          <th className="pb-2">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingClaims.map((claim) => (
                          <tr key={claim.id} className="border-b border-gray-700">
                            <td className="pr-4 py-2">{translateItemName(claim.item_name)}</td>
                            <td className="pr-4 py-2">{claim.claimed_by_username}</td>
                            <td className="py-2 flex gap-2">
                              <button onClick={() => handleClaimApprove(claim)} className="px-2 py-1 bg-green-700 hover:bg-green-600 rounded text-xs">Approve</button>
                              <button onClick={() => handleClaimReject(claim)} className="px-2 py-1 bg-red-700 hover:bg-red-600 rounded text-xs">Reject</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              );
            })()}
            {claimsActionError && <p className="text-red-400 mt-3">{claimsActionError}</p>}
          </div>
        </div>
      )}

      {/* Modal 1: Lights Out Solver */}
      {showModal1 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-4 pb-4">
          <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-xl mx-3 max-h-[95vh] overflow-y-auto">
            <LightsOutSolver isModal={true} onClose={() => setShowModal1(false)} />
          </div>
        </div>
      )}

      {/* Modal 2: Paint Tracker */}
      {showModal2 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
          <div className="bg-white p-6 rounded shadow-md text-black max-h-[90vh] overflow-y-auto">
            <PaintingSolver isModal={true} onClose={() => setShowModal2(false)} />
          </div>
        </div>
      )}

      {/* Modal 3: Memory puzzle */}
      {showModal3 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-4 pb-4">
          <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-lg mx-3 max-h-[95vh] overflow-y-auto">
            <Grid isModal={true} onClose={() => setShowModal3(false)} />
          </div>
        </div>
      )}

      {/* Modal 4: Key Puzzle Solver */}
      {showModal4 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-4 pb-4">
          <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-3xl mx-3 max-h-[95vh] overflow-y-auto">
            <RushHourSolverPage isModal={true} onClose={() => setShowModal4(false)} />
          </div>
        </div>
      )}

      {/* Modal 5: Slider Puzzle Solver */}
      {showModal5 && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-4 pb-4">
          <div className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-2xl mx-3 max-h-[95vh] overflow-y-auto">
            <SliderSolverPage isModal={true} onClose={() => setShowModal5(false)} />
          </div>
        </div>
      )}

      {/* DLC / Entitlements picker -- pops up when a locked item is clicked */}
      {showDlcModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-16"
          onClick={() => setShowDlcModal(false)}
        >
          <div
            className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-3 p-4 text-white max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-2">
              <h2 className="text-lg font-bold">Do you own this?</h2>
              <button
                onClick={() => setShowDlcModal(false)}
                className="text-gray-400 hover:text-white text-xl leading-none ml-2"
              >
                ×
              </button>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              {dlcModalReqIds.length > 0 && (
                <>
                  This item needs:{" "}
                  <strong>
                    {dlcModalReqIds
                      .map((id) => requirementsMap[id])
                      .filter(Boolean)
                      .map((req) => `${req.name}${req.platform ? ` (${req.platform})` : ""}`)
                      .join(", ")}
                  </strong>
                  . Check off anything you own below and it unlocks right away.
                </>
              )}
            </p>
            {Object.values(requirementsMap).map((req) => {
              const owned = myRequirementIds.includes(req.id);
              return (
                <label key={req.id} className="flex items-start gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={owned}
                    onChange={() => handleToggleRequirement(req.id, owned)}
                    className="mt-1 h-4 w-4 shrink-0"
                  />
                  <div>
                    <div className="font-medium">
                      {req.name}
                      {req.platform ? ` (${req.platform})` : ""}
                    </div>
                    <div className="text-xs text-gray-400">
                      {req.is_available
                        ? req.requirement_type === "dlc"
                          ? "Still available for purchase"
                          : "Still available"
                        : req.requirement_type === "preorder"
                        ? "No longer available — preorder only"
                        : req.requirement_type === "early access"
                        ? "No longer available — early access only"
                        : "No longer available"}
                    </div>
                  </div>
                </label>
              );
            })}
            <button
              onClick={() => setShowDlcModal(false)}
              className="w-full mt-2 p-2 rounded bg-blue-600 hover:bg-blue-700 text-sm font-semibold"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Filters panel -- events, item type, entitlements, status, and the
          display toggles, all behind one button so the page stays clean */}
      {showFiltersPanel && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-start z-50 overflow-y-auto pt-8 pb-8"
          onClick={() => setShowFiltersPanel(false)}
        >
          <div
            className="bg-gray-900 rounded-xl shadow-2xl w-full max-w-md mx-3 text-white max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-4 pt-4 pb-3 border-b border-gray-700">
              <h2 className="text-lg font-bold">Events &amp; Filters</h2>
              <div className="flex items-center gap-4">
                <button onClick={clearAllFilters} className="text-xs text-blue-400 hover:text-blue-300 font-semibold">
                  Clear all
                </button>
                <button
                  onClick={() => setShowFiltersPanel(false)}
                  className="text-gray-400 hover:text-white text-xl leading-none"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-2">
              {/* Status */}
              <div className="py-3 border-b border-gray-800">
                <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Status</div>
                <div className="flex bg-gray-800 border border-gray-700 rounded-lg p-1 gap-1">
                  <button
                    onClick={handleToggleShowUnchecked}
                    className={`flex-1 text-xs font-semibold py-1.5 rounded ${showUncheckedOnly ? "bg-blue-600" : "text-gray-300 hover:bg-gray-700"}`}
                  >
                    Unclaimed
                  </button>
                  <button
                    onClick={handleToggleShowChecked}
                    className={`flex-1 text-xs font-semibold py-1.5 rounded ${showCheckedOnly ? "bg-blue-600" : "text-gray-300 hover:bg-gray-700"}`}
                  >
                    Claimed
                  </button>
                  <button
                    onClick={() => { setShowUncheckedOnly(false); setShowCheckedOnly(false); }}
                    className={`flex-1 text-xs font-semibold py-1.5 rounded ${!showUncheckedOnly && !showCheckedOnly ? "bg-blue-600" : "text-gray-300 hover:bg-gray-700"}`}
                  >
                    All
                  </button>
                </div>
              </div>

              {/* Events */}
              <div className="py-3 border-b border-gray-800">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400">
                    Events <span className="normal-case font-normal text-gray-500">&mdash; pick any number</span>
                  </div>
                  <button
                    onClick={() => toggleAllEvents(allEventsSelected, allEventNames)}
                    className="text-xs text-blue-400 hover:text-blue-300 font-semibold shrink-0"
                  >
                    {allEventsSelected ? "Deselect all" : "Select all"}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-3">
                  {allEventNames.map((event) => (
                    <label key={event} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedEventNames.includes(event)}
                        onChange={() => toggleEventFilter(event)}
                        className="h-4 w-4 shrink-0"
                      />
                      {translateEvent(event)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Item Type */}
              {allTypeNames.length > 0 && (
                <div className="py-3 border-b border-gray-800">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Item Type</div>
                  <div className="grid grid-cols-2 gap-x-3">
                    {allTypeNames.map((type) => (
                      <label key={type} className="flex items-center gap-2 py-1 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={selectedTypes.includes(type)}
                          onChange={() => toggleTypeFilter(type)}
                          className="h-4 w-4 shrink-0"
                        />
                        {translateType(type)}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Entitlements / DLC */}
              {Object.values(requirementsMap).length > 0 && (
                <div className="py-3 border-b border-gray-800">
                  <div className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Entitlements &amp; DLC</div>
                  {Object.values(requirementsMap).map((req) => (
                    <label key={req.id} className="flex items-start gap-2 py-1.5 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedEntitlementFilterIds.includes(req.id)}
                        onChange={() => toggleEntitlementFilter(req.id)}
                        className="h-4 w-4 mt-0.5 shrink-0"
                      />
                      <span>
                        {req.name}
                        {req.platform ? ` (${req.platform})` : ""}
                        <span className="block text-xs text-gray-500">
                          {req.is_available
                            ? req.requirement_type === "dlc"
                              ? "Still available for purchase"
                              : "Still available"
                            : req.requirement_type === "preorder"
                            ? "No longer available — preorder only"
                            : req.requirement_type === "early access"
                            ? "No longer available — early access only"
                            : "No longer available"}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )}

              {/* Toggles */}
              <div className="py-3">
                <label className="flex items-center justify-between py-2 cursor-pointer text-sm">
                  <span>
                    New this event only
                    <span className="block text-xs text-gray-500">Hide anything previously released</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={newOnly}
                    onChange={() => setNewOnly(!newOnly)}
                    className="h-4 w-4 shrink-0"
                  />
                </label>
                <label className="flex items-center justify-between py-2 cursor-pointer text-sm">
                  <span>
                    Hide unobtainable items
                    <span className="block text-xs text-gray-500">Preorder / early-access items you missed</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={hideUnobtainable}
                    onChange={async () => {
                      const next = !hideUnobtainable;
                      setHideUnobtainable(next);
                      const token = await getTokenInternal();
                      if (token) {
                        fetch(
                          `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/hide-unobtainable`,
                          {
                            method: "PUT",
                            headers: {
                              Authorization: `Bearer ${token.access_token}`,
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ hide_unobtainable: next }),
                          }
                        );
                      }
                    }}
                    className="h-4 w-4 shrink-0"
                  />
                </label>
                <label className="flex items-center justify-between py-2 cursor-pointer text-sm">
                  <span>Show item images</span>
                  <input
                    type="checkbox"
                    checked={showItemImages}
                    onChange={async () => {
                      const next = !showItemImages;
                      setShowItemImages(next);
                      const token = await getTokenInternal();
                      if (token) {
                        fetch(
                          `${process.env.REACT_APP_API_HOST}/api/accounts/${encodeURIComponent(token.account.username)}/show-item-images`,
                          {
                            method: "PUT",
                            headers: {
                              Authorization: `Bearer ${token.access_token}`,
                              "Content-Type": "application/json",
                            },
                            body: JSON.stringify({ show_item_images: next }),
                          }
                        );
                      }
                    }}
                    className="h-4 w-4 shrink-0"
                  />
                </label>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-700">
              <button
                onClick={() => setShowFiltersPanel(false)}
                className="w-full py-2 rounded bg-blue-600 hover:bg-blue-700 font-semibold text-sm"
              >
                Show {totalItemsCount} items
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Solvers Dropdown Button */}
<div className="fixed bottom-4 right-4 z-[9999] pointer-events-auto" ref={dropdownRef}>
  <button
    onClick={() => setDropdownOpen((prev) => !prev)} // Always toggle the dropdown
    className="px-3 py-2 rounded shadow-md text-sm bg-blue-600 text-white" // Always active and styled
    title="Solvers"
  >
    Solvers
  </button>

  {dropdownOpen && (
    <div className="absolute bottom-10 right-0 w-48 bg-white text-black shadow-md rounded z-50">
      <button
        onClick={() => {
          setShowModal1(true);
          setShowModal2(false);
          setShowModal3(false);
          setDropdownOpen(false);
        }}
        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
      >
        Open Lights Out Solver
      </button>
      <button
        onClick={() => {
          setShowModal2(true);
          setShowModal1(false);
          setShowModal3(false);
          setDropdownOpen(false);
        }}
        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
      >
        Manor Painting Helper
      </button>
      <button
        onClick={() => {
          setShowModal3(true);
          setShowModal1(false);
          setShowModal2(false);
          setShowModal4(false);
          setShowModal5(false);
          setDropdownOpen(false);
        }}
        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
      >
        Matching/Memory Helper
      </button>
      <button
        onClick={() => {
          setShowModal4(true);
          setShowModal1(false);
          setShowModal2(false);
          setShowModal3(false);
          setShowModal5(false);
          setDropdownOpen(false);
        }}
        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
      >
        Key Puzzle Solver
      </button>
      <button
        onClick={() => {
          setShowModal5(true);
          setShowModal1(false);
          setShowModal2(false);
          setShowModal3(false);
          setShowModal4(false);
          setDropdownOpen(false);
        }}
        className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
      >
        Slider Puzzle Solver
      </button>
    </div>
  )}
</div>
    </div>
  );


}
